/**
 * Data Prefetcher
 * Prefetches API data in background for instant page transitions
 *
 * Strategy:
 * - When user lands on Dashboard, prefetch Matches and set React Query cache
 *   so Matches page shows data instantly (LinkedIn-style).
 * - When user lands on Matches, prefetch Applications data
 * - Use requestIdleCallback for non-blocking prefetch
 * - INSTANT LOAD: Persist matches to localStorage for instant render after browser restart
 */

import type { QueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { invokeEdgeFunction } from "@/lib/edgeFunctionClient";
import { getProfileVersionHash } from "@/hooks/useProfileVersion";

// Cache to prevent duplicate prefetches
const prefetchCache = new Set<string>();

// Store prefetched data in memory for instant access
const dataCache: Map<string, { data: unknown; timestamp: number }> = new Map();

// Cache TTL: 2 minutes (memory), 24 hours (localStorage)
const CACHE_TTL = 2 * 60 * 1000;
const LOCALSTORAGE_TTL = 24 * 60 * 60 * 1000; // 24 hours
const MATCHES_STORAGE_KEY = "findigo_matches_cache";

/**
 * INSTANT LOAD: Save matches to localStorage for instant render on next visit
 */
const saveMatchesToStorage = (userId: string, matches: unknown[], timestamp: number) => {
  try {
    const data = JSON.stringify({ userId, matches: matches.slice(0, 10), timestamp }); // Keep first 10 for size
    localStorage.setItem(MATCHES_STORAGE_KEY, data);
  } catch {
    // Storage full or unavailable - ignore
  }
};

/**
 * INSTANT LOAD: Get matches from localStorage if fresh enough
 */
export const getMatchesFromStorage = (userId: string): unknown[] | null => {
  try {
    const stored = localStorage.getItem(MATCHES_STORAGE_KEY);
    if (!stored) return null;
    
    const { userId: storedUserId, matches, timestamp } = JSON.parse(stored);
    if (storedUserId !== userId) return null;
    if (Date.now() - timestamp > LOCALSTORAGE_TTL) {
      localStorage.removeItem(MATCHES_STORAGE_KEY);
      return null;
    }
    
    return matches;
  } catch {
    return null;
  }
};

/**
 * Check if we have cached data that's still fresh
 */
export const getCachedData = <T>(key: string): T | null => {
  const cached = dataCache.get(key);
  if (!cached) return null;

  if (Date.now() - cached.timestamp > CACHE_TTL) {
    dataCache.delete(key);
    return null;
  }

  return cached.data as T;
};

/**
 * Prefetch daily matches and set React Query cache so Matches page shows data instantly.
 * Called when user is on Dashboard; same queryKey as useDailyMatches so data is used on navigation.
 */
export const prefetchMatches = async (queryClient: QueryClient | null) => {
  if (prefetchCache.has("matches")) return;

  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;

    prefetchCache.add("matches");

    const scheduleIdleWork = window.requestIdleCallback || ((cb: () => void) => setTimeout(cb, 100));

    scheduleIdleWork(async () => {
      try {
        const { data: profileVersion } = await supabase.rpc("get_profile_version", {
          p_user_id: session.user.id,
        });
        const versionHash = getProfileVersionHash(profileVersion ?? null);

        const response = await invokeEdgeFunction<{
          matches: unknown[];
          jobsAnalyzed: number;
          totalMatches: number;
          hasMore: boolean;
          offset?: number;
          limit?: number;
          cached?: boolean;
        }>("calculate-matches", {
          body: {
            limit: 25,
            offset: 0,
            profileVersion: profileVersion ?? null,
            detailLevel: "list",
          },
          timeout: 25000,
          maxRetries: 0,
        });

        if (response.ok && response.data) {
          const d = response.data;
          const payload = {
            matches: d.matches ?? [],
            jobsAnalyzed: d.jobsAnalyzed ?? 0,
            totalMatches: d.totalMatches ?? 0,
            hasMore: d.hasMore ?? false,
            offset: d.offset ?? 0,
            limit: d.limit ?? 25,
            cached: d.cached,
          };
          if (queryClient) {
            queryClient.setQueryData(["daily-matches", session.user.id, versionHash], payload);
          }
          const now = Date.now();
          dataCache.set("matches", { data: payload.matches, timestamp: now });
          // INSTANT LOAD: Persist to localStorage for instant render on next visit
          saveMatchesToStorage(session.user.id, payload.matches as unknown[], now);
        }
      } catch {
        prefetchCache.delete("matches");
      }
    });
  } catch {
    // Silent fail
  }
};

/**
 * Prefetch applications data
 * Called when user navigates to see their applications ready
 */
export const prefetchApplications = async () => {
  if (prefetchCache.has("applications")) return;
  
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;
    
    prefetchCache.add("applications");
    
    const scheduleIdleWork = window.requestIdleCallback || ((cb: () => void) => setTimeout(cb, 100));
    
    scheduleIdleWork(async () => {
      try {
        // Use job_applications (has job_title, applied_at). Omit destination – not in schema (causes 400).
        const { data, error } = await supabase
          .from("job_applications")
          .select("id, job_id, job_title, company_name, status, applied_at, created_at")
          .eq("user_id", session.user.id)
          .order("applied_at", { ascending: false })
          .limit(20);
        
        if (!error && data) {
          dataCache.set("applications", {
            data,
            timestamp: Date.now(),
          });
        }
      } catch {
        prefetchCache.delete("applications");
      }
    });
  } catch {
    // Silent fail
  }
};

/**
 * Prefetch user profile data
 */
export const prefetchProfile = async () => {
  if (prefetchCache.has("profile")) return;
  
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;
    
    prefetchCache.add("profile");
    
    const scheduleIdleWork = window.requestIdleCallback || ((cb: () => void) => setTimeout(cb, 100));
    
    scheduleIdleWork(async () => {
      try {
        const { data, error } = await supabase
          .from("profiles")
          .select("*")
          .eq("user_id", session.user.id)
          .single();
        
        if (!error && data) {
          dataCache.set("profile", {
            data,
            timestamp: Date.now(),
          });
        }
      } catch {
        prefetchCache.delete("profile");
      }
    });
  } catch {
    // Silent fail
  }
};

/**
 * Clear all cached data (call on logout)
 */
export const clearPrefetchCache = () => {
  prefetchCache.clear();
  dataCache.clear();
  try {
    localStorage.removeItem(MATCHES_STORAGE_KEY);
  } catch {
    // Ignore
  }
};

/**
 * Prefetch data based on current route.
 * Pass queryClient so matches prefetch populates React Query (instant load on Matches page).
 *
 * ONE-REQUEST RULE: Do NOT prefetch on /matches. Matches page must use exactly one data request
 * (matches-initial) before first real match card. Prefetch would count as a second request and fail E2E.
 *
 * P0 RATE LIMIT: Do NOT prefetch calculate-matches on / or /dashboard. dashboard-initial already
 * calls calculate-matches server-side for Pro users; a second call from the browser burns rate limit
 * and can cause 429 so "inga jobb visas" (findigo.se must show jobs). Prefetch only when on other routes.
 */
export const prefetchForRoute = (currentPath: string, queryClient: QueryClient | null = null) => {
  switch (currentPath) {
    case "/":
      // Skip prefetch on / and /dashboard to avoid double calculate-matches (429) and ensure jobs show
      prefetchProfile();
      break;
    case "/dashboard":
      prefetchProfile();
      break;
    case "/matches":
      // Intentionally no prefetch: only matches-initial is allowed before first paint (AGENTS.md).
      break;
    case "/settings":
      prefetchProfile();
      break;
    default:
      break;
  }
};
